import React, { useContext, useState } from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import {
  Radar as RadarIcon,
  Lightbulb as LightbulbIcon,
  MenuBook as MenuBookIcon,
  Edit as PenToolIcon,
  Send as SendIcon,
  Share as ShareIcon,
  Speed as SpeedIcon,
  Hearing as HearingIcon,
  ArrowForward as ArrowRightIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { UserContext } from "../contexts/UserContext";
import { VIEW_PATHS } from "../routing/appRoutes";

interface WorkflowStage {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  description: string;
  color: string;
  view?: 'news' | 'research' | 'library' | 'dashboard' | 'distribution' | 'idea_sparker' | 'social_studio' | 'social_listening' | 'fast_tracking';
  /** Permission resource the user needs "read" on to see this card (unset = everyone). */
  requiresRead?: string;
}

const workflowStages: WorkflowStage[] = [
  {
    icon: RadarIcon,
    title: "Content Radar",
    subtitle: "Monitoring",
    description: "Track trending topics and discover content opportunities in real-time.",
    color: "linear-gradient(135deg, #12c2e9 0%, #c471ed 100%)",
    view: "news",
    requiresRead: "content_radar",
  },
  {
    icon: LightbulbIcon,
    title: "Idea Sparker",
    subtitle: "Story selection and ideation",
    description: "Turn the clusters this profile is tracking into story ideas: a headline, a short brief, and angles to explore.",
    color: "linear-gradient(135deg, #c471ed 0%, #f64f59 100%)",
    view: "idea_sparker",
    requiresRead: "idea_sparker",
  },
  {
    icon: SpeedIcon,
    title: "Fast tracking",
    subtitle: "Automate publishing",
    description: "Automate content generation and publishing from trusted sources.",
    color: "linear-gradient(135deg, #22c55e 0%, #14b8a6 100%)",
    view: "fast_tracking",
    requiresRead: "analytic_profiles",
  },
  {
    icon: MenuBookIcon,
    title: "Research",
    subtitle: "Notebooks & linked drafts",
    description: "Add custom content and research in your notebooks.",
    color: "linear-gradient(135deg, #6366f1 0%, #22d3ee 100%)",
    view: "research",
    requiresRead: "notebooks",
  },
  {
    icon: PenToolIcon,
    title: "Draft Creator",
    subtitle: "Movies, recipes, events & more",
    description: "AI tooling to draft and refine your content.",
    color: "linear-gradient(135deg, #f64f59 0%, #ff9a56 100%)",
    view: "library",
    requiresRead: "notebooks",
  },
  {
    icon: ShareIcon,
    title: "Social Studio",
    subtitle: "Campaigns & short-form clips",
    description: "Generate social media campaigns to promote your content.",
    color: "linear-gradient(135deg, #818cf8 0%, #22d3ee 100%)",
    view: "social_studio",
    requiresRead: "social_studio",
  },
  {
    icon: HearingIcon,
    title: "Social Listening",
    subtitle: "Handles, topics & sentiment",
    description: "Listen to handles and hashtags, score sentiment, and tune phrases per profile.",
    color: "linear-gradient(135deg, #34d399 0%, #3b82f6 100%)",
    view: "social_listening",
    requiresRead: "social_listening",
  },
  {
    icon: SendIcon,
    title: "Distribution Hub",
    subtitle: "Publishing & Distribution",
    description: "Publish your content across multiple platforms simultaneously.",
    color: "linear-gradient(135deg, #feca57 0%, #12c2e9 100%)",
    view: "distribution",
    requiresRead: "notebooks",
  },
];

/**
 * Home cards. Each card with a view is a real <a href> (router link), so Cmd/Ctrl/middle-click opens
 * the view in a new tab and "Copy link" works. Cards the user can't access aren't rendered at all.
 */
export function Features() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { user } = useContext(UserContext);
  const visibleStages = workflowStages.filter(
    (stage) => !stage.requiresRead || Boolean(user?.permissions?.[stage.requiresRead]?.read)
  );

  return (
    <section style={{ padding: '80px 0' }}>
      <Box sx={{ maxWidth: '1100px', margin: '0 auto', px: 4 }}>
        {user && visibleStages.length === 0 && (
          // A user with only the basics (e.g. just created) has no feature to open yet.
          <Box role="status" sx={{ textAlign: 'center', color: '#a1a1aa', py: 6 }}>
            <Typography variant="h6" sx={{ color: '#fafafa', mb: 1 }}>
              No features enabled yet
            </Typography>
            <Typography variant="body2">
              Your account doesn't have access to any Flock features yet. Ask your administrator to enable the ones you need.
            </Typography>
          </Box>
        )}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 4
        }}>
          {visibleStages.map((stage, index) => (
            <Card
              key={stage.title}
              {...(stage.view ? { component: RouterLink, to: VIEW_PATHS[stage.view] } : {})}
              className={`relative overflow-hidden transition-all duration-300 hover:shadow-lg${stage.view ? " cursor-pointer" : ""}`}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              sx={{
                display: 'block',
                textDecoration: 'none',
                border: hoveredIndex === index ? "1px solid rgba(102, 126, 234, 0.5)" : "1px solid rgba(255, 255, 255, 0.1)",
                backgroundColor: "#1a1a1a",
                position: "relative",
                height: '100%',
                minHeight: '280px',
                maxWidth: '500px',
                margin: '0 auto',
                opacity: stage.view ? 1 : 0.5,
                cursor: stage.view ? 'pointer' : 'default',
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: stage.color,
                  opacity: hoveredIndex === index && stage.view ? 0.05 : 0,
                  transition: "opacity 0.3s",
                  pointerEvents: "none",
                },
              }}
            >
              <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Icon badge */}
                <Box display="flex" alignItems="start" mb={2}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      height: 48,
                      width: 48,
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "12px",
                      background: stage.color,
                      transition: "transform 0.3s",
                      transform: hoveredIndex === index ? "scale(1.1)" : "scale(1)",
                    }}
                  >
                    <stage.icon sx={{ fontSize: 24, color: "#fff" }} />
                  </Box>
                </Box>

                {/* Content */}
                <Box mb={2}>
                  <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 700, color: "#fff" }}>
                    {stage.title}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: "#667eea" }}>
                    {stage.subtitle}
                  </Typography>
                </Box>

                <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6, color: "#aaa", flexGrow: 1 }}>
                  {stage.description}
                </Typography>

                {/* Hover arrow indicator */}
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#667eea",
                    opacity: hoveredIndex === index ? 1 : 0,
                    transform: hoveredIndex === index ? "translateX(0)" : "translateX(-8px)",
                    transition: "all 0.3s",
                  }}
                >
                  <span>Explore</span>
                  <ArrowRightIcon sx={{ fontSize: 16 }} />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </section>
  );
}
